// workspace.ts — Endpoints du Workspace (tuiles personnelles).
//
// Un seul appel au chargement : `getWorkspaceTiles()` renvoie la disposition
// ET les données. `ids` sert le chargement en deux temps du mobile (ce qui est
// au-dessus de la ligne de flottaison d'abord), et le rafraîchissement ciblé
// après une mutation — jamais un appel par tuile.

import { req } from './_internals.js';

export interface TileLanding {
  page: string;
  facets: Record<string, unknown>;
}

export interface WorkspaceTile {
  id: number;
  key: string;
  size: string;
  params: Record<string, unknown>;
  data?: Record<string, unknown>;
  landing?: TileLanding;
  /** Atterrissages par compteur, quand ils diffèrent de celui de la tuile. */
  landings?: Record<string, TileLanding>;
  /** Droit retiré depuis la pose : affichée grisée, jamais escamotée. */
  revoked?: boolean;
  /** La tuile prend un paramètre : c'est le catalogue qui le dit, pas sa clé. */
  parametrable?: boolean;
}

/** Une valeur admise par un paramètre — projet piloté, équipe dirigée. */
export interface TileParamOption {
  id: number;
  code: string | null;
  name: string | null;
}

export interface TileParamSchema {
  name: string;
  type: string;
  required?: boolean;
  /**
   * Les valeurs que CET utilisateur peut choisir, calculées par le serveur.
   * Le front ne construit jamais sa propre liste : elle finirait par proposer
   * un choix que l'enregistrement refuse.
   */
  options: TileParamOption[];
}

export interface CatalogEntry {
  key: string;
  permission: string;
  sizes: string[];
  default: boolean;
  invalidated_by: string[];
  params_schema: TileParamSchema[];
}

export interface LayoutItem {
  id?: number;
  tile_key: string;
  size: string;
  params: Record<string, unknown>;
}

export const workspaceApi = {
  getWorkspaceTiles: (ids?: number[]) =>
    req<{ tiles: WorkspaceTile[] }>(
      'GET',
      '/workspace/tiles' + (ids && ids.length ? '?tiles=' + ids.join(',') : ''),
    ),
  getWorkspaceCatalog: () => req<{ catalog: CatalogEntry[] }>('GET', '/workspace/catalog'),
  // Remplacement intégral : le réordonnancement touche tout, et un
  // remplacement est idempotent. Les `id` déjà posés sont renvoyés pour que
  // les identifiants d'instance survivent (sinon `?tiles=<ids>` casserait).
  putWorkspaceTiles: (tiles: LayoutItem[]) =>
    req<{ tiles: WorkspaceTile[] }>('PUT', '/workspace/tiles', { tiles }),
};
